import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";

import { dirname } from "../../../../util/resourcePath";
import { useRouter } from "../../../../util/router";
import { useUser } from "./../../../../util/user";
import GYAxios from "../../../../util/GYAxios";
import Noti from "../../../../util/noti";

import ClientUsersTable from "./ClientUsersTable";
import DefaultSelect from "../../../general/DefaultSelect";
import NoUsersComp from "./NoUsersComp";
import { useClientUsersFormContext } from "../../../../util/clientUsersFormContext";
import { getGYUserCountry, isITUser, isManagementUser } from "../../../../util/roles";

const ClientUsersContent = () => {
	const user = useUser();
	const router = useRouter();

	const directory = dirname();
	const [reload, setReload] = useState(true);
	const [userHasEditRole, setUserHasEditRole] = useState(false);
	const [resetSitesSelect, setResetSitesSelect] = useState(false);
	const [resetClientSelect, setResetClientSelect] = useState(false);
	const [isLoadingUsers, setIsLoadingUsers] = useState(true);
	const [clientIsSelected, setClientIsSelected] = useState(false);
	const [siteIsSelected, setSiteIsSelected] = useState(false);
	const [countriesSelectOptions, setCountriesSelectOptions] = useState([]);
	const [clientSelectOptions, setClientSelectOptions] = useState([]);

	const defaultCountry = ((isITUser(user.roles) || isManagementUser(user.roles)) && "ALL") || getGYUserCountry(user.roles) || "THL";
	const [country, setCountry] = useState(defaultCountry);

	const [selectedClient, setSelectedClient] = useState("ALL");
	const [selectedSite, setSelectedSite] = useState("ALL");
	const [clientUsers, setClientUsers] = useState([]);
	const [clientSitesSelectOptions, setClientSitesSelectOptions] = useState([]);

	// client users form context
	const { selectedClientUsers, setSelectedClientUsers } = useClientUsersFormContext();

	const defaultCountrytOpt = {
		value: "ALL",
		label: "All Countries",
	};
	const defaultClientOpt = {
		value: "ALL",
		label: "All Clients",
	};
	const defaultSitesOpt = {
		value: "ALL",
		label: "All Sites",
	};

	const selectStyle = {
		container: (provided, state) => ({
			...provided,
			marginRight: 0,
			width: "100%",
			float: "right",
		}),
		option: (provided, state) => ({
			...provided,
			fontSize: "13px",
		}),
		menu: (provided) => ({ ...provided, zIndex: 9999 }),
		menuList: (provided, state) => ({
			...provided,
			maxHeight: "500px",
			color: "black",
			textAlign: "left",
		}),
		groupHeading: (provided, state) => ({
			...provided,
			fontSize: "13px",
			color: "#a4aaaa",
			borderTop: "#a4aaaa 1px solid",
		}),
		singleValue: (provided, state) => {
			const fontSize = "13px";
			return { ...provided, fontSize };
		},
	};

	const btnStyle = { width: "30%", minWidth: "100px", margin: "5px 0 0 5px" };

	/**
	 * Fetch countries select
	 */
	const fetchCountries = () => {
		GYAxios.get("/select/countries").then((res) => {
			const countries = !res.data.error ? res.data : [];
			setCountriesSelectOptions(countries);
			setReload(false);
		});
	};

	/**
	 * Fetch clients select of selected country
	 * @param {string} country
	 */
	const fetchClients = (country) => {
		// Reset client and site select
		setResetClientSelect(true);
		setResetSitesSelect(true);

		const clientsURI =
			country === "ALL"
				? "/select/clients/include/lip"
				: `/select/clients/include/lip/${country}`;

		GYAxios.get(clientsURI).then((res) => {
			const resData = !res.data.error ? res.data : [];
			let clients = country === "ALL" ? resData[0] : resData;

			const roles = user.roles;
			const isGYITUser = roles.includes("_GY:IT");

			if (!isGYITUser)
				clients = clients.filter(
					(client) =>
						client.value != 27 && client.value != 123 && client.value != 125
				);

			const clientSelectOpts = [defaultClientOpt, ...clients];

			setClientSelectOptions(clientSelectOpts);
			setReload(false);

			setResetClientSelect(false);
			setResetSitesSelect(false);
		});
	};

	/**
	 * Fetch sites select of selected client
	 * @param {string} clientID
	 */
	const fetchClientSites = (clientID) => {
		setResetSitesSelect(true);

		const sitesSelectURI = `/select/sites/client/${clientID}`;

		GYAxios.get(sitesSelectURI).then((res) => {
			const sites = !res.data.error ? res.data : [];
			const opts = sites.length > 1 ? [defaultSitesOpt, ...sites] : [sites[0]];

			setClientSitesSelectOptions(opts);
			setResetSitesSelect(false);
		});
	};

	/**
	 * Fetch users based on filters selected
	 * @param {string} country
	 * @param {string} clientID
	 * @param {string} siteID
	 */
	const fetchUsers = (country, clientID, siteID) => {
		setIsLoadingUsers(true);

		if (siteID != "ALL") setSiteIsSelected(true);
		else setSiteIsSelected(false);

		let usersURI = "";

		// Site is selected
		if (siteID !== "ALL")
			usersURI = `/users/singlesite/filter?client=${clientID}&site=${siteID}`;
		// Client is selected
		else if (clientID !== "ALL")
			usersURI = `/users/filter?column=client_id&value=${clientID}`;
		// Country is selected
		else if (country !== "ALL")
			usersURI = `/users/filter?country=${country}&column=type&value=2`;
		// No filters are selected
		else usersURI = "/users/filter?column=type&value=2";

		GYAxios.get(usersURI)
			.then((res) => {
				const users = !res.data.error ? res.data : [];

				setClientUsers(users);
				setIsLoadingUsers(false);
			})
			.catch((err) => {
				console.log("err :", err);
			});
	};
	/**
	 * Set selected country
	 * @param {string} country
	 */
	const handleOnCountryChange = (country) => {
		const countryStr = country.value;
		setCountry(countryStr);
	};

	/**
	 * Set selected client
	 * @param {string} client
	 */
	const handleOnChange = (client) => {
		const clientID = client.value;
		setSelectedClient(clientID);
	};

	/**
	 * Set selected site
	 * @param {string} site
	 */
	const handleOnSiteChange = (site) => {
		const siteID = site.value;
		setSelectedSite(siteID);
	};

	/**
	 * Redirect to users form
	 * @param {string} client
	 * @param {string} site
	 * @param {string} formType
	 */
	const redirectToForm = (client, site, formType) => {
		const siteURI = site !== "ALL" ? `/site/${site}` : "";
		const redirectURI =
			formType === "EDIT"
				? `/solar/administration/users/clients/form/edit/client/${client}${siteURI}`
				: `/solar/administration/users/clients/form/new/client/${client}${siteURI}`;

		router.push(redirectURI);
	};

	useEffect(() => {
		const roles = user.roles;
		const userHasEditRole = roles.includes(
			"administration:ClientUsersContent:_edit"
		);
		setUserHasEditRole(userHasEditRole);
	}, [user]);

	useEffect(() => {
		if (reload) {
			fetchCountries();
			fetchClients(country);
			fetchUsers(country, selectedClient, selectedSite);
		}
	}, [reload]);

	useEffect(() => {
		setSelectedClient("ALL");
		setClientIsSelected(false);
		fetchClients(country);
		setSelectedClientUsers(null);
	}, [country]);

	useEffect(() => {
		setSelectedSite("ALL");
		setSiteIsSelected(false);
		setSelectedClientUsers(null);

		if (selectedClient != "ALL") {
			setClientIsSelected(true);
			fetchClientSites(selectedClient);
		} else {
			setClientIsSelected(false);
			setClientSitesSelectOptions([]);
		}
	}, [selectedClient]);

	useEffect(() => {
		fetchUsers(country, selectedClient, selectedSite);
	}, [country, selectedClient, selectedSite]);

	return (
		<div className="perf-table panel panel-default">
			<div className="panel-heading ">
				<Row>
					<Col sm="7" md="7" lg="7">
						<h2 className="panel-title">Client Users</h2>
					</Col>
					<Col sm="5" md="5" lg="5" style={{ textAlign: "right" }}>
						<img
							src={`${directory}/images/reload.png`}
							onClick={() => setReload(true)}
							style={{
								height: "35px",
								cursor: "pointer",
							}}
							alt="logo"
						/>
					</Col>
				</Row>
				<Row>
					<Col sm="4" md="4" lg="4">
						{clientIsSelected && userHasEditRole && (
							<>
								<Button
									variant="warning"
									style={btnStyle}
									onClick={() =>
										redirectToForm(selectedClient, selectedSite, "NEW")
									}
								>
									Add Users
								</Button>
								{clientUsers && clientUsers.length > 0 && (
									<Button
										variant="warning"
										style={btnStyle}
										onClick={() =>
											redirectToForm(selectedClient, selectedSite, "EDIT")
										}
										disabled={!selectedClientUsers}
									>
										Edit Users
									</Button>
								)}
							</>
						)}
					</Col>
					<Col md="2">
						<label>Country select</label>
						<DefaultSelect
							style={selectStyle}
							onChange={(e) => {
								if (e.value !== country) handleOnCountryChange(e);
							}}
							options={countriesSelectOptions}
							defaultValue={defaultCountrytOpt}
							isReadonly={false}
							isDefault={true}
							value={countriesSelectOptions.find(c => c.value === country)}
						/>
					</Col>
					{!resetClientSelect && (
						<Col md="3">
							<label>Client select</label>
							<DefaultSelect
								style={selectStyle}
								onChange={(e) => {
									if (e.value !== selectedClient) handleOnChange(e);
								}}
								defaultOptions={clientSelectOptions}
								options={clientSelectOptions}
								defaultValue={defaultClientOpt}
								isReadonly={false}
								isDefault={true}
							/>
						</Col>
					)}
					{!resetSitesSelect && (
						<Col md="3">
							<label>Site select</label>
							<DefaultSelect
								style={selectStyle}
								onChange={(e) => {
									if (e.value !== selectedSite) handleOnSiteChange(e);
								}}
								options={clientSitesSelectOptions}
								defaultValue={defaultSitesOpt}
								isReadonly={selectedClient == "ALL"}
								isDefault={true}
							/>
						</Col>
					)}
				</Row>
			</div>
			{clientUsers.length > 0 || siteIsSelected ? (
				<ClientUsersTable users={clientUsers} isLoadingUsers={isLoadingUsers} usersSelectable={selectedClient !== 'ALL'}/>
			) : (
				<NoUsersComp
					redirectToForm={redirectToForm}
					userHasEditRole={userHasEditRole}
					selectedClient={selectedClient}
				/>
			)}
			<Noti />
		</div>
	);
};

export default ClientUsersContent;
